<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRoute } from 'vue-router';
import { useAbility } from '@casl/vue';

import { stateShortNames } from '@/utils/PortalConstants';
import TableActionBtn from '@/components/btns/TableActionBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import DashboardSearchForm from '@/components/searchForms/DashboardSearchForm.vue';
import DashboardTable from '@/components/tables/DashboardTable.vue';
import SmartDashboardTabs from '@/components/tabs/SmartDashboardTabs.vue';
import SmartDashboardVWindow from '@/components/vWindows/SmartDashboardVWindow.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import usePrimaryTrackingDashboardViewData from '@/composables/data/usePrimaryTrackingDashboardViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDialog from '@/composables/useDialog';
import useDownloadCsv from '@/composables/useDownloadCsv';
import { useJobEditDialog } from '@/composables/useJobEditDialog';
import usePrimaryTrackingDashboardView from '@/composables/views/usePrimaryTrackingDashboardView';
import { useTabsStore } from '@/stores/useTabsStore';
import { attributesItems, statusesItems } from '@/constants/fieldItems';
import * as routeNames from '@/constants/routeNames';

const route = useRoute();
const ability = useAbility();
const isStaff = computed(() => ability.can('staff', 'Job'));

const { getCurrentQueryDate } = useCurrentDateString();
const {
  queryDate,
  queryStates,
  querySearch,
  queryDrivers,
  queryStatuses,
  queryAttributes,
} = useCommonRouteQuery({
  queryDate: getCurrentQueryDate(),
  queryStates: stateShortNames, // TODO same variables in handleClearAllFiltersClick()
  querySearch: '',
  queryDrivers: [],
  queryStatuses: [],
  queryAttributes: [],
});

const {
  // isFetchingAllData,
  isFetchingDashboardJobs,
  allData,
  refreshDashboardJobs,
} = usePrimaryTrackingDashboardViewData(queryDate);

const dashboardJobsData = computed(() => allData.value?.dashboardJobs);

const { isDownloadingCSV, downloadCsv } = useDownloadCsv();
const { openDialog } = useDialog();
const tabsStore = useTabsStore();

const {
  search,
  initialData,
  total,
  results,
  resultsForCsv,
  driversItems,
  tabsBadges,
} = usePrimaryTrackingDashboardView(
  queryDate,
  queryStates,
  querySearch,
  queryDrivers,
  queryStatuses,
  queryAttributes,
  openDialog,
  isStaff,
  toRef(tabsStore.tabs, 'dashboard'),
  dashboardJobsData,
  isFetchingDashboardJobs,
);

const jobEditDialog = useJobEditDialog();

function handleEditClick(jobId: number, jobDate: string): void {
  jobEditDialog.open(jobId, jobDate, results.value, refreshDashboardJobs);
}
</script>

<template>
  <div
    :class="$style.primaryTrackingDashboardView"
    data-testid="primaryTrackingDashboardView"
  >
    <PageContentWrapper :class="$style.pageContentWrapper">
      <FormWrapper
        v-if="route.name === routeNames.JOBS"
        :class="$style.formWrapper"
      >
        <DashboardSearchForm
          :initial-data="initialData"
          :loading="isFetchingDashboardJobs"
          :class="$style.form"
          :drivers-items="driversItems"
          :statuses-items="statusesItems"
          :attributes-items="attributesItems"
          :is-staff="isStaff"
          @submit="search"
        />
      </FormWrapper>
      <div :class="$style.tabsAndRefresh">
        <div :class="$style.refreshDataBtnWrapper">
          <TableActionBtn
            v-if="route.name === routeNames.JOBS"
            :class="$style.refreshDataBtn"
            icon="mdi-autorenew"
            :loader-size="18"
            :loading="isFetchingDashboardJobs"
            @click="refreshDashboardJobs"
          >
            Refresh data
          </TableActionBtn>
        </div>
        <SmartDashboardTabs
          :class="$style.dashboardTabs"
          :badges="tabsBadges"
        />
      </div>
      <TablePageShadowWrapper
        v-if="queryDate"
        :class="$style.tablePageShadowWrapper"
      >
        <SmartDashboardVWindow :class="$style.windowItems">
          <template #default="{ tabValue, tabKey, tabIndex }">
            <DashboardTable
              v-if="tabKey === tabsStore.tabs.dashboard /* unmounting */"
              :loading="isFetchingDashboardJobs"
              :data="results"
              :date="queryDate"
              :is-staff="isStaff"
              @edit-click="handleEditClick"
            />
            <TablePageFooter v-if="results?.length" :total="total" no-pages>
              <ExportAsCsvIconBtn
                :disabled="isFetchingDashboardJobs || !results?.length"
                :loading="isDownloadingCSV"
                @click="downloadCsv(resultsForCsv, 'primaryTracking')"
              />
            </TablePageFooter>
          </template>
        </SmartDashboardVWindow>
      </TablePageShadowWrapper>
    </PageContentWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.primaryTrackingDashboardView {
}
  .pageContentWrapper {
    margin-top: 1rem;
  }
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
  }
    .windowItems {
      overflow: visible;
    }
  .tabsAndRefresh {
    position: relative;
    margin-top: 0.5rem;
    margin-bottom: 8px;

    &:after {
      content: "";
      clear: both;
      display: block;
    }
  }
    .refreshDataBtnWrapper {
      height: 48px;
      display: flex;
      align-items: center;
      float: right;
    }
      .refreshDataBtn {
        font-size: 18px;
      }
    .dashboardTabs {
      float: left;
    }
</style>
